import React, { Suspense, useEffect, useMemo, useState } from "react";
import queryString from "query-string";
import moment from "moment";
import { Link } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import LoadingSpinner from "../../components/UI/loadingSpinner/LoadingSpinner";
import { ROUTES } from "../../constants/routes";
import { downloadCSV } from "../../utils/csv";
import {
  attendanceHeaderMapping,
  attendanceHeaders,
  PAGINATION_SEQUENCE,
} from "../../constants";
import ExportIcon from "../../components/icons/ExportIcon";
import { getApi } from "../../services/http.service";
import { IAttendance } from "../../interfaces/attendance.interface";
import { STATES_OPTIONS } from "../../constants/states";
import DateRangeDropdown from "../../components/UI/date-range-dropdown/DateRangeDropdown";
import Dropdown from "../../components/UI/dropdown/Dropdown";

function Attendances() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [type, setType] = useState<"logged" | "unlogged">(
    searchParams.get("type") || ("logged" as any)
  );
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [perPage, setPerPage] = useState(
    Number(searchParams.get("limit") || 10)
  );
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data & Search
  const [state, setState] = useState(searchParams.get("state") || "");
  const [startDate, setStartDate] = useState(
    searchParams.get("start") || moment().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    searchParams.get("end") || moment().format("YYYY-MM-DD")
  );
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<IAttendance[]>([]);

  const [sortField, setSortField] = useState<string>(
    searchParams.get("sort") || "updatedAt"
  );
  const [sortAsc, setSortAsc] = useState<boolean>(
    searchParams.get("order") === "asc"
  );

  useEffect(() => {
    fetchNews(page, perPage, search);
  }, [page, perPage, search, state, startDate, endDate, type]);

  const fetchNews = async (page: number, limit: number, search: string) => {
    try {
      setLoading(true);
      const result = await getApi(ROUTES.GET_ALL_ATTENDANCE, {
        page,
        limit,
        search,
        startDate,
        endDate,
        state,
        status: type==='logged'?'present':'absent'
      });
      if (result.code === 200) {
        setFilteredData(result?.data);
        setTotalRows(result.total); // Set total records
      }
      const queryParams = queryString.stringify({
        sort: sortField,
        order: sortAsc ? "asc" : "desc",
        page,
        limit: perPage,
        state,
        type,
        start: startDate,
        end: endDate,
      });
      navigate(`?${queryParams}`, { replace: true });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Page Change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const queryParams = queryString.stringify({
      sort: sortField,
      order: sortAsc ? "asc" : "desc",
      page: newPage,
      limit: perPage,
      state,
      type,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  };

  // Handle Rows per Page Change
  const handlePerRowsChange = (newPerPage: number, newPage: number) => {
    setPerPage(newPerPage);
    setPage(newPage);
    const queryParams = queryString.stringify({
      sort: sortField,
      order: sortAsc ? "asc" : "desc",
      page: newPage,
      limit: newPerPage,
      state,
      type,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  };

  const columns: TableColumn<IAttendance>[] = useMemo(
    () => [
      {
        id: "fullName",
        name: "Employee",
        cell: (row: any) => (
          <Link
            className="nav-link text-hover-underline"
            to={`/employees/${row.employee?._id}`}
          >
            {row?.employee?.fullName}
          </Link>
        ),
        selector: (row: any) => row.employee?.fullName,
        sortable: true,
        maxWidth: "250px",
        sortField: "fullName",
      },
      {
        id: "state",
        name: "State",
        selector: (row: any) => row.employee?.address?.state,
        sortable: true,
        width: "250px",
        sortField: "state",
      },
      {
        name: "City",
        selector: (row: any) => row.employee?.address?.city,
        sortable: true,
        width: "250px",
        sortField: "city",
      },
      {
        id: "reporter",
        name: "Reporter",
        selector: (row: any) => row.employee?.reporters?.[0]?.fullName || "-",
        sortable: true,
        width: "250px",
        sortField: "reporter",
      },
      {
        id: "updatedAt",
        name: "Logged At",
        selector: (row: any) => moment(row?.updatedAt).format("hh:mm A"),
        sortable: true,
        width: "250px",
        sortField: "updatedAt",
      },
    ],
    []
  );

  async function selectedChangeHandler(e: any) {
    const s = e[0].toString();
    const en = e[1]?.toString();
    if (s && en) {
      setStartDate(s);
      setEndDate(en);
      const queryParams = queryString.stringify({
        sort: sortField,
        order: sortAsc ? "asc" : "desc",
        page,
        limit: perPage,
        state: e.target.value,
        type,
        start: s,
        end: en,
      });
      navigate(`?${queryParams}`, { replace: true });
    }
  }

  function stateChangeHandler(e: React.ChangeEvent<HTMLSelectElement>) {
    setState(() => e.target.value);
    const queryParams = queryString.stringify({
      sort: sortField,
      order: sortAsc ? "asc" : "desc",
      page,
      limit: perPage,
      state: e.target.value,
      type,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  }

  const handleSort = (column: TableColumn<IAttendance>, direction: any) => {
    const key = column.sortField?.toString() || "";
    setSortField(key);
    setSortAsc(direction === "asc");
    const queryParams = queryString.stringify({
      sort: key,
      order: direction,
      page,
      limit: perPage,
      state,
      type,
      start: startDate,
      end: endDate,
    });
    navigate(`?${queryParams}`, { replace: true });
  };
  return (
    <section>
      <div className="d-flex justify-content-between flex-wrap align-items-center">
        <h2 className="title">Attendances</h2>
        <div className="d-flex flex-wrap ">
          <Dropdown
            dropdownData={STATES_OPTIONS}
            bindLabel="state"
            bindValue="state"
            selected={state}
            label="State"
            onChange={stateChangeHandler}
          />
          <DateRangeDropdown onChange={selectedChangeHandler} />
          <span className="ms-2">
            <button
              className="btn btn-primary"
              onClick={() => {
                downloadCSV(
                  filteredData,
                  attendanceHeaders,
                  attendanceHeaderMapping,
                  "Groove_Attendance"
                );
              }}
            >
              <ExportIcon />
              <span className="ps-1"> Export</span>
            </button>
          </span>
        </div>
      </div>
      <ul className="nav nav-tabs mb-1">
        <li className="nav-item">
          <button
            className={`nav-link btn px-5 me-2 ${
              type === "logged" ? "active active_tab" : "inactive_tab"
            }`}
            disabled={loading}
            onClick={() => setType("logged")}
          >
            Present Employee
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link btn px-5 me-2 ${
              type === "unlogged" ? "active active_tab" : "inactive_tab"
            }`}
            disabled={loading}
            onClick={() => setType("unlogged")}
          >
            Absent Employee
          </button>
        </li>
      </ul>
      <Suspense>
        <DataTable
          columns={columns }
          data={filteredData}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGINATION_SEQUENCE}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          striped
          progressPending={loading}
          progressComponent={<LoadingSpinner />}
          onSort={handleSort}
          sortServer={false}
          defaultSortFieldId={sortField}
          defaultSortAsc={sortAsc}
        />
      </Suspense>
    </section>
  );
}

export default Attendances;
